/**
 * Multi-trade orders — grouping for the client's order list.
 *
 * A client ordering a painter AND an electrician for the same object creates
 * ONE order in their head but N Job documents on the server (one per trade),
 * tied together by `orderGroupId`. Pricing, invoicing and assignment all stay
 * per-job — grouping is purely how the list reads back to the person who
 * placed it, so they see the order they submitted rather than its parts.
 *
 * Jobs with no `orderGroupId` are single-trade orders and form a group of one,
 * which keeps every caller on a single code path.
 */

import type { JobShift } from '@rendimees24/shared-types';

import { coverageSummary } from './coverage';

export interface GroupableJob {
  id: string;
  orderGroupId?: string;
  requirements?: {
    roleRequired?: string;
    workerCount?: number;
    startAt?: string;
    endAt?: string;
    shift?: JobShift | null;
  };
  assignedWorkerIds?: string[];
  assignments?: Array<{ id?: string; workerId: string; startAt?: string; endAt?: string }> | null;
  pricing?: { expectedRevenueEur?: number | null };
}

export interface OrderGroup<T extends GroupableJob> {
  /** Stable React key: the group id, or the lone job's id. */
  key: string;
  /** The job whose object/date/status the card header shows. */
  primary: T;
  /** Every job in the group, in list order. `[primary]` when ungrouped. */
  jobs: T[];
  /** Sum of expected revenue across the group; null when no job priced. */
  totalRevenueEur: number | null;
}

/**
 * Collapse a job list into order groups, preserving the incoming order: a group
 * takes the position of its FIRST job, so re-grouping never reshuffles a list
 * the caller already sorted.
 */
export function groupOrders<T extends GroupableJob>(jobs: T[]): OrderGroup<T>[] {
  const groups: OrderGroup<T>[] = [];
  const byId = new Map<string, OrderGroup<T>>();

  for (const job of jobs) {
    const gid = job.orderGroupId;
    const existing = gid ? byId.get(gid) : undefined;
    if (existing) {
      existing.jobs.push(job);
      const rev = job.pricing?.expectedRevenueEur;
      if (rev != null) existing.totalRevenueEur = (existing.totalRevenueEur ?? 0) + rev;
      continue;
    }
    const rev = job.pricing?.expectedRevenueEur;
    const group: OrderGroup<T> = {
      key: gid ?? job.id,
      primary: job,
      jobs: [job],
      totalRevenueEur: rev ?? null,
    };
    groups.push(group);
    if (gid) byId.set(gid, group);
  }

  return groups;
}

/**
 * "3 × Maaler · 1 × Elektrik" — the trade breakdown line. Roles are labelled by
 * the caller's translator, since skill ids are not human-readable.
 */
export function describeTrades<T extends GroupableJob>(
  group: OrderGroup<T>,
  label: (roleId: string) => string,
): string {
  return group.jobs
    .map((j) => {
      const role = label(j.requirements?.roleRequired ?? '');
      const n = j.requirements?.workerCount ?? 1;
      return n > 1 ? `${n} × ${role}` : role;
    })
    .join(' · ');
}

/**
 * Staffing across a whole order: places asked for, and how many of the
 * order's shifts have every place filled.
 *
 * EPIC 56 — this used to add up names (`assignedWorkerIds.length`), which
 * read "2/2" for two people who both work Mon–Tue of a Mon–Fri order. The
 * shift count is what tells someone at a glance whether the order still
 * needs attention.
 */
export function groupWorkerTotals<T extends GroupableJob>(
  group: OrderGroup<T>,
): { required: number; coveredShifts: number; totalShifts: number; shortShifts: number } {
  let required = 0;
  let coveredShifts = 0;
  let totalShifts = 0;
  let shortShifts = 0;
  for (const j of group.jobs) {
    required += j.requirements?.workerCount ?? 1;
    const c = coverageSummary(j);
    coveredShifts += c.coveredShifts;
    totalShifts += c.totalShifts;
    shortShifts += c.shortShifts;
  }
  return { required, coveredShifts, totalShifts, shortShifts };
}
